import {
  RemovedParticipantsMetadata,
  AddedParticipantsMetadata,
  ChatLogType,
  Message,
  MessagePreview,
  Member,
  ChatParticipant,
  Chat,
  ChatType,
  EditedGroupMetadata,
  ChatParticipantRole,
  PromotedAdminsMetadata,
  DemotedAdminsMetadata,
  TransferredOwnershipMetadata,
} from '@tribeplatform/gql-client/types'
import { PendingMessage } from '@tribeplatform/react-sdk/hooks'
import { noop } from '@tribeplatform/react-ui-kit/hooks'

import { I18nShape } from '../../../i18n/types.js'

interface GetLastMessageAuthorNameParams {
  lastMessage: MessagePreview | Message
  intl: I18nShape
  authMember?: Member
}

interface GetParticipantsNamesParams {
  participants: ChatParticipant[]
  intl: I18nShape
  authMember?: Member
  authorId?: string
}

/**
 * Check if the chat is a saved chat.
 * Saved chat is a direct message with only one participant and the participant is the current user.
 */
export const isChatToMyself = (chat: Chat) => {
  const isDm = chat.type === ChatType.Direct
  const isSameAuthor =
    chat.currentParticipant?.memberId === chat.createdById &&
    !chat.otherParticipant

  return isSameAuthor && isDm
}

export const getLastMessageAuthorName = ({
  lastMessage,
  intl: { $t },
  authMember,
}: GetLastMessageAuthorNameParams): string => {
  if (!lastMessage) {
    return ''
  }

  if (lastMessage.authorId === authMember?.id) {
    return $t({
      id: 'Generics.You',
      defaultMessage: 'You',
    })
  }

  return lastMessage.authorName
}

const getParticipantsNames = ({
  participants,
  intl: { $t, formatList },
  authMember,
  authorId,
}: GetParticipantsNamesParams) => {
  return formatList(
    participants
      // We don't want to have a message like "You added you and [participant ...] to the group"
      .filter(participant => participant.memberId !== authorId)
      .map(participant => {
        if (participant.memberId === authMember?.id) {
          return $t({
            id: 'Generics.you',
            defaultMessage: 'you',
          })
        }
        return participant.name
      }),
    {
      type: 'conjunction',
    },
  )
}

export const isPendingMessage = (
  message: Message | PendingMessage,
): message is PendingMessage => {
  return 'status' in message && message.status === 'pending'
}

export const chatLogMessageFactory: (
  intl: I18nShape,
) => (
  message: Message | MessagePreview,
  authMember?: Member,
) => string[] = intl => {
  const { $t } = intl
  return (message: Message | MessagePreview, authMember?: Member) => {
    if (!message) {
      return []
    }

    const authorName = getLastMessageAuthorName({
      lastMessage: message,
      intl,
      authMember,
    })

    switch (message.meta?.type) {
      case ChatLogType.CreatedGroup: {
        const message = $t(
          {
            id: 'Messaging.Chat.ChatLog.CreatedGroup',
            defaultMessage: '{hand_emoji} {authorName} created the group',
          },
          { authorName, hand_emoji: '👋' },
        )
        return [message]
      }
      case ChatLogType.EditedGroup: {
        const meta = message.meta as EditedGroupMetadata
        const messages = []
        if (meta.name) {
          messages.push(
            $t(
              {
                id: 'Messaging.Chat.ChatLog.EditedGroupName',
                defaultMessage:
                  '{authorName} updated the group name to {name}.',
              },
              { authorName, name: meta.name },
            ),
          )
        }

        if (meta.avatarUrl) {
          messages.push(
            $t(
              {
                id: 'Messaging.Chat.ChatLog.EditedGroupAvatar',
                defaultMessage: '{authorName} updated the group photo.',
              },
              { authorName },
            ),
          )
        }
        return messages
      }
      case ChatLogType.AddedParticipants: {
        const addedParticipants =
          (message.meta as AddedParticipantsMetadata)?.addedParticipants ?? []
        const participants = getParticipantsNames({
          participants: addedParticipants,
          intl,
          authMember,
          authorId: message.authorId,
        })
        const msg = $t(
          {
            id: 'Messaging.Chat.ChatLog.AddedParticipants',
            defaultMessage: '{authorName} added {participants} to the group',
          },
          { authorName, participants },
        )
        return [msg]
      }
      case ChatLogType.RemovedParticipants: {
        const removedParticipants =
          (message.meta as RemovedParticipantsMetadata)?.removedParticipants ??
          []
        const participants = getParticipantsNames({
          participants: removedParticipants,
          intl,
          authMember,
        })
        const msg = $t(
          {
            id: 'Messaging.Chat.ChatLog.RemovedParticipants',
            defaultMessage:
              '{authorName} removed {participants} from the group',
          },
          { authorName, participants },
        )
        return [msg]
      }
      case ChatLogType.LeftGroup: {
        const msg = $t(
          {
            id: 'Messaging.Chat.ChatLog.LeftGroup',
            defaultMessage: '{authorName} left the group',
          },
          { authorName },
        )
        return [msg]
      }
      case ChatLogType.PromotedAdmins: {
        const promotedParticipants = (message.meta as PromotedAdminsMetadata)
          ?.promotedParticipants
        const participants = getParticipantsNames({
          participants: promotedParticipants,
          intl,
          authMember,
        })
        const msg = $t(
          {
            id: 'Messaging.Chat.ChatLog.PromotedAdmins',
            defaultMessage: '{authorName} promoted {participants} to admin',
          },
          { authorName, participants },
        )
        return [msg]
      }
      case ChatLogType.DemotedAdmins: {
        const demotedParticipants = (message.meta as DemotedAdminsMetadata)
          ?.demotedParticipants
        const participants = getParticipantsNames({
          participants: demotedParticipants,
          intl,
          authMember,
        })
        const msg = $t(
          {
            id: 'Messaging.Chat.ChatLog.DemotedAdmins',
            defaultMessage: '{authorName} demoted {participants} from admin',
          },
          { authorName, participants },
        )

        return [msg]
      }
      case ChatLogType.TransferredOwnership: {
        const newOwner = (message.meta as TransferredOwnershipMetadata)
          ?.newOwner
        const msg = $t(
          {
            id: 'Messaging.Chat.ChatLog.TransferredOwnership',
            defaultMessage:
              '{authorName} transferred the ownership of the group to {newOwner}',
          },
          { authorName, newOwner: newOwner.name },
        )
        return [msg]
      }
      default: {
        const exhaustiveCheck: never = message.meta?.type
        return exhaustiveCheck
      }
    }
  }
}

export const isLegacyMessage = (message: Message | PendingMessage) => {
  return !isPendingMessage(message) && !!message.legacyId
}

export const participantRoleFactory =
  ({ $t }: I18nShape) =>
  (role: ChatParticipantRole) => {
    switch (role) {
      case ChatParticipantRole.Admin:
        return $t({
          id: 'Generics.Admin',
          defaultMessage: 'Admin',
        })
      case ChatParticipantRole.Owner:
        return $t({
          id: 'Generics.Owner',
          defaultMessage: 'Owner',
        })
      case ChatParticipantRole.Member:
        return null
      default: {
        const exhaustiveCheck: never = role
        return exhaustiveCheck
      }
    }
  }

export const copyMessageContentIntoClipboard = async (content?: string) => {
  try {
    await navigator.clipboard.write([
      new ClipboardItem({
        'text/html': new Blob([content || ''], {
          type: 'text/html',
        }),
      }),
    ])
  } catch (error) {
    noop(error)
  }
}

interface MessageEditedParams {
  originalMessage: Message
  newContent?: string
  newAttachmentIds?: string[]
  newEmbedIds?: string[]
}

export const isMessageEdited = ({
  originalMessage,
  newContent,
  newAttachmentIds = [],
  newEmbedIds = [],
}: MessageEditedParams): boolean => {
  const contentChanged = newContent !== originalMessage.content

  if (contentChanged) {
    return true
  }

  const originalAttachmentIds = [
    ...(originalMessage.attachmentIds || []),
    ...(originalMessage.imageIds || []),
  ]
  const attachmentsLengthChanged =
    newAttachmentIds.length !== originalAttachmentIds.length
  const attachmentsChanged = !originalAttachmentIds.every(id =>
    newAttachmentIds.includes(id),
  )

  if (attachmentsLengthChanged || attachmentsChanged) {
    return true
  }

  const originalEmbedIds = originalMessage.embedIds || []
  const embedsLengthChanged = newEmbedIds.length !== originalEmbedIds.length
  const embedsChanged = !originalEmbedIds.every(id => newEmbedIds.includes(id))

  if (embedsLengthChanged || embedsChanged) {
    return true
  }

  return false
}
